import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  DEVICE_TYPES,
  MODEL_CLIENT_TYPES,
  MODEL_DETAIL_KEYS,
  MODEL_IMAGE_SLOTS,
  MODEL_TEXT_FIELDS,
  PHONE_CAPABILITIES,
  PHONE_TYPES,
  RECORD_STATUSES,
  productModelInputSchema,
} from '@app/shared';
import type { ProductModelDto, ProductModelInput } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { FormDialog } from '../../components/dialogs';
import {
  CheckboxInput,
  MoneyInput,
  NumberInput,
  SelectInput,
  TextInput,
  YesNoInput,
  applyServerErrors,
  toOptions,
} from '../../components/form';
import { useFeatureReleased } from '../../release';
import { useCarriers } from '../plans/lookups';
import { useCreateProductModel, useUpdateProductModel } from './api';
import {
  CATALOGUE_IMAGES_FEATURE,
  ImageSlots,
  imageUrl,
  modelImagePath,
  saveImageDraft,
} from './CatalogueImages';
import type { ImageDraft } from './CatalogueImages';

const TEXT_DEFAULTS = Object.fromEntries(MODEL_TEXT_FIELDS.map(([key]) => [key, ''])) as Record<
  (typeof MODEL_TEXT_FIELDS)[number][0],
  string
>;
/** Release switch of Phone Capable, Handover, E-SIM Compatible and the fields after them. */
const MODEL_DETAILS = 'model-details-2';

/** The four long descriptions get a field of their own row. */
const LONG_TEXT = MODEL_TEXT_FIELDS.filter(([, , max]) => max > 100);
const SHORT_TEXT = MODEL_TEXT_FIELDS.filter(([, , max]) => max <= 100);

const EMPTY: ProductModelInput = {
  make: '',
  model: '',
  modelNumber: '',
  sku: '',
  isSim: false,
  deviceType: null,
  phoneType: null,
  networkType: '',
  simType: '',
  os: '',
  color: '',
  grade: '',
  // Left empty so the field shows its placeholder; the schema stores 0 when nothing is entered.
  amountCents: undefined,
  inventoryThreshold: null,
  showOnWebsite: false,
  ram: '',
  storage: '',
  screen: '',
  camera: '',
  carrierId: null,
  csrEquipmentPhoneType: false,
  websiteEquipmentPhoneType: false,
  ...TEXT_DEFAULTS,
  phoneCapable: null,
  handover: false,
  esimCompatible: false,
  clientType: null,
  position: null,
  activationFeeCents: undefined,
  copayCents: undefined,
  shortDescription: '',
  noteDescription: '',
  status: 'Active',
};

const toForm = (m: ProductModelDto): ProductModelInput => ({
  make: m.make,
  model: m.model,
  modelNumber: m.modelNumber,
  sku: m.sku,
  isSim: m.isSim,
  deviceType: m.deviceType,
  phoneType: m.phoneType,
  networkType: m.networkType,
  simType: m.simType,
  os: m.os,
  color: m.color,
  grade: m.grade,
  amountCents: m.amountCents,
  inventoryThreshold: m.inventoryThreshold,
  showOnWebsite: m.showOnWebsite,
  ram: m.ram,
  storage: m.storage,
  screen: m.screen,
  camera: m.camera,
  carrierId: m.carrierId,
  csrEquipmentPhoneType: m.csrEquipmentPhoneType,
  websiteEquipmentPhoneType: m.websiteEquipmentPhoneType,
  ...(Object.fromEntries(MODEL_TEXT_FIELDS.map(([key]) => [key, m[key]])) as typeof TEXT_DEFAULTS),
  phoneCapable: m.phoneCapable,
  handover: m.handover,
  esimCompatible: m.esimCompatible,
  clientType: m.clientType,
  position: m.position,
  activationFeeCents: m.activationFeeCents,
  copayCents: m.copayCents,
  shortDescription: m.shortDescription,
  noteDescription: m.noteDescription,
  status: m.status,
});

interface Props {
  open: boolean;
  /** Present when editing. */
  model?: ProductModelDto;
  onClose: () => void;
}

/** "Add Model" and, when editing, the reference's "Update Model Details" dialog. */
export function ModelFormDialog({ open, model, onClose }: Props) {
  const notify = useNotify();
  const createModel = useCreateProductModel();
  const updateModel = useUpdateProductModel();
  const carriers = useCarriers(open);
  const queryClient = useQueryClient();
  const featureOn = useFeatureReleased();
  const [images, setImages] = useState<ImageDraft>({});
  const { control, handleSubmit, reset, setError } = useForm<ProductModelInput>({
    defaultValues: EMPTY,
    resolver: zodResolver(productModelInputSchema),
  });

  useEffect(() => {
    if (open) {
      reset(model ? toForm(model) : EMPTY);
      setImages({});
    }
  }, [open, model, reset]);

  const submit = handleSubmit(async (form) => {
    // While the switch is off the newer fields are not sent, so a save leaves them as they are.
    const values = featureOn(MODEL_DETAILS)
      ? form
      : (Object.fromEntries(
          Object.entries(form).filter(([key]) => !(MODEL_DETAIL_KEYS as string[]).includes(key)),
        ) as ProductModelInput);
    try {
      const saved = model
        ? await updateModel.mutateAsync({ id: model.id, input: values })
        : await createModel.mutateAsync(values);
      // The pictures go once the model is saved, so a new model has its id.
      if (Object.keys(images).length) {
        await saveImageDraft(images, (slot) => modelImagePath(saved.id, slot));
        await queryClient.invalidateQueries({ queryKey: ['product-models'] });
      }
      notify.success(model ? 'Model updated.' : 'Model added.');
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  return (
    <FormDialog
      open={open}
      title={model ? 'Update Model Details' : 'Add Model'}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={createModel.isPending || updateModel.isPending}
      submitLabel={model ? 'UPDATE' : 'Submit'}
      maxWidth="md"
    >
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
          pt: 1,
        }}
      >
        <TextInput control={control} name="make" label="Make" required />
        <TextInput control={control} name="model" label="Model" required />
        <TextInput control={control} name="modelNumber" label="Model Number" />
        <SelectInput
          control={control}
          name="carrierId"
          label="Carrier"
          emptyLabel="Select"
          options={(carriers.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
        />
        <TextInput
          control={control}
          name="sku"
          label="SKU"
          helperText="Used by the Add Bulk Unit file"
        />
        <SelectInput
          control={control}
          name="deviceType"
          label="Device Type"
          emptyLabel="Select"
          options={toOptions(DEVICE_TYPES)}
        />
        <SelectInput
          control={control}
          name="phoneType"
          label="Phone Type"
          emptyLabel="Select"
          options={toOptions(PHONE_TYPES)}
        />
        <MoneyInput control={control} name="amountCents" label="Amount" />
        <NumberInput
          control={control}
          name="inventoryThreshold"
          label="Inventory Threshold"
          emptyValue={null}
        />
        <SelectInput
          control={control}
          name="status"
          label="Status"
          options={toOptions(RECORD_STATUSES)}
        />
        <TextInput control={control} name="networkType" label="Network Type" />
        <TextInput control={control} name="simType" label="SIM Type" />
        <TextInput control={control} name="os" label="OS" />
        <TextInput control={control} name="grade" label="Grade" />
        <TextInput control={control} name="color" label="Color" />
        <TextInput control={control} name="ram" label="RAM" />
        <TextInput control={control} name="storage" label="Inbuilt Memory" />
        <TextInput control={control} name="screen" label="Screen" />
        <TextInput control={control} name="camera" label="Camera" />
        <YesNoInput control={control} name="showOnWebsite" label="Show Model on Website" />
        <YesNoInput
          control={control}
          name="csrEquipmentPhoneType"
          label="CSR Equipment Phone Type"
        />
        <YesNoInput
          control={control}
          name="websiteEquipmentPhoneType"
          label="Website Equipment Phone Type"
        />
        {SHORT_TEXT.map(([key, label, max]) => (
          <TextInput
            key={key}
            control={control}
            name={key}
            label={label}
            slotProps={{ htmlInput: { maxLength: max } }}
          />
        ))}
        {featureOn(MODEL_DETAILS) ? (
          <>
            <SelectInput
              control={control}
              name="phoneCapable"
              label="Phone Capable"
              emptyLabel="Select"
              options={toOptions(PHONE_CAPABILITIES)}
            />
            <YesNoInput control={control} name="handover" label="Handover" />
            <YesNoInput control={control} name="esimCompatible" label="E-SIM Compatible" />
            <SelectInput
              control={control}
              name="clientType"
              label="Client Type"
              emptyLabel="Select"
              options={toOptions(MODEL_CLIENT_TYPES)}
            />
            <NumberInput control={control} name="position" label="Position" emptyValue={null} />
            <MoneyInput
              control={control}
              name="activationFeeCents"
              label="Activation Fees (In $)"
            />
            <MoneyInput control={control} name="copayCents" label="Co-pay Amount (In $)" />
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextInput
                control={control}
                name="shortDescription"
                label="Short Description"
                slotProps={{ htmlInput: { maxLength: 250 } }}
              />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextInput
                control={control}
                name="noteDescription"
                label="Note / Description"
                multiline
                minRows={3}
                slotProps={{ htmlInput: { maxLength: 2000 } }}
              />
            </Box>
          </>
        ) : null}
        {/* Assumption: the reference has no such switch. It marks SIM products, which have no device attributes. */}
        <CheckboxInput control={control} name="isSim" label="SIM product" />
        {LONG_TEXT.map(([key, label, max]) => (
          <Box key={key} sx={{ gridColumn: '1 / -1' }}>
            <TextInput
              control={control}
              name={key}
              label={label}
              multiline
              minRows={2}
              slotProps={{ htmlInput: { maxLength: max } }}
            />
          </Box>
        ))}
        {featureOn(CATALOGUE_IMAGES_FEATURE) ? (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <ImageSlots
              slots={MODEL_IMAGE_SLOTS.map((slot) => {
                const stored = model?.images.find((i) => i.slot === slot);
                return {
                  slot,
                  label: `Image ${slot}`,
                  url: model && stored ? imageUrl(modelImagePath(model.id, slot), stored) : null,
                };
              })}
              draft={images}
              onChange={setImages}
            />
          </Box>
        ) : null}
        <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
          Insurance, EMI, Discount and Discount Validity are catalogue notes: nothing is charged,
          insured or discounted from them.
          {featureOn(MODEL_DETAILS)
            ? ' Activation Fees and Co-pay Amount are recorded on the model; they are not charged automatically.'
            : ''}
        </Typography>
      </Box>
    </FormDialog>
  );
}
